// ─────────────────────────────────────────────────────────────────────────────
// Intl — the only place in this system that names a locale to a formatter.
//
// DIGITS ARE LATIN. `123`, never the Eastern Arabic form of it, whatever
// language the interface is in. (The counter-example is not written out here,
// because audit check 34 forbids the literal in source — including this file.)
//
// This is not a stylistic preference. `Intl.NumberFormat("ar-SA")` defaults to
// the arab numbering system, because that is what CLDR says Saudi Arabia's
// default is — so the moment anybody passed a bare "ar-SA" anywhere, salaries,
// balances, day counts, record numbers and page numbers switched script with
// the interface language. A payslip going to a bank is the case that decides
// it: the figure on it has to be the figure the bank reads.
//
// The fix is the `-u-nu-latn` Unicode extension, which pins the numbering
// system and changes NOTHING else about the locale — Arabic still sorts,
// pluralises, and names its months in Arabic. Because it is baked into the tag
// rather than applied at a call site, anything that formats through these tags
// inherits it and cannot opt out by forgetting.
//
// `scripts/audit.mjs` check 34 (`latin-digits`) keeps the numbers that way —
// no `Intl.NumberFormat` outside this file, and no Eastern Arabic numeral
// typed straight into a string. Check 35 (`one-date-format`) does the same for
// dates: no `Intl.DateTimeFormat` and no `toLocale*` outside `dates.ts`, which
// takes its tag from here.
// ─────────────────────────────────────────────────────────────────────────────

/** The two languages the interface speaks. Mirrors `Locale` in `web/i18n`. */
export type UiLocale = "en" | "ar";

/**
 * The BCP-47 tag handed to every `Intl` constructor in this system, with the
 * numbering system pinned to Latin.
 *
 * Never write "ar-SA" or "en-US" at a call site — take the tag from here.
 */
export const INTL_LOCALE: Readonly<Record<UiLocale, string>> = {
  en: "en-US-u-nu-latn",
  ar: "ar-SA-u-nu-latn",
};

/**
 * A machine-neutral tag for formatting that must not vary with the reader at
 * all — the wall-clock projection in `dates.ts`, whose output is parsed rather
 * than read.
 */
export const NEUTRAL_INTL_LOCALE = "en-CA-u-nu-latn";

/**
 * A number for display. The one number formatter in the system.
 *
 * Grouping and decimals follow the locale's own conventions; only the digits
 * are fixed.
 */
export function formatNumber(
  value: number,
  locale: UiLocale = "en",
  options?: Intl.NumberFormatOptions,
): string {
  const safe = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat(INTL_LOCALE[locale], options).format(safe);
}
